import { classifyError } from '../error-kind'
import type { ErrorKind } from '../error-kind'
import type { DomainDto } from '../../dtos/domain'
import type { AccessUrl } from './url'

export type AccessContext = { kind: 'domain'; domainId: number } | { kind: 'org'; orgId: number }

export interface AccessResolved {
    status: 'resolved'
    context: AccessContext
    /** The open domain, or for an org context the org's first domain by display name. */
    domain: DomainDto
    /** Set when the URL named nothing; the owner never navigates, the URL sync does. */
    redirectTo: string | undefined
}

export interface AccessUnavailable {
    status: 'unavailable'
    /** One screen serves both reasons; the user is never told which. */
    reason: 'not_found' | 'forbidden'
}

export interface AccessFailed {
    status: 'failed'
    kind: ErrorKind
    error: unknown
}

export type AccessResolution =
    | { status: 'idle' }
    | { status: 'resolving'; previous: AccessResolved | undefined }
    | AccessResolved
    | AccessUnavailable
    | AccessFailed

export type AccessResult = AccessResolved | AccessUnavailable | AccessFailed

/** The part of a user's domain record that resolution reads. */
export interface AccessDomainRecord {
    id: number
    accountId: number
    displayName: string
}

export interface ResolveAccessInput {
    url: AccessUrl
    domains: readonly AccessDomainRecord[]
    fetchDomain: (domainId: number) => Promise<DomainDto>
    lastUsedDomainId: number | undefined
}

const FORBIDDEN: AccessUnavailable = { status: 'unavailable', reason: 'forbidden' }

/** Same order the domain selector uses for an org's domains; id breaks ties so the pick is stable. */
const byDisplayName = (a: AccessDomainRecord, b: AccessDomainRecord): number =>
    a.displayName.toLowerCase().localeCompare(b.displayName.toLowerCase()) || a.id - b.id

type Fetched = { ok: true; domain: DomainDto } | { ok: false; result: AccessUnavailable | AccessFailed }

async function fetchDetail(fetchDomain: ResolveAccessInput['fetchDomain'], domainId: number): Promise<Fetched> {
    try {
        return { ok: true, domain: await fetchDomain(domainId) }
    } catch (error) {
        const kind = classifyError(error)
        if (kind === 'not_found' || kind === 'forbidden') {
            return { ok: false, result: { status: 'unavailable', reason: kind } }
        }
        return { ok: false, result: { status: 'failed', kind, error } }
    }
}

/**
 * Resolves what a URL opens. A domain can be fetched by id, so a domain the list does not name is
 * requested and the server's 403 or 404 decides. An organization has no fetch on this path: its
 * pages make their own requests and the server answers those for a user who may not see it. An org
 * URL therefore always opens the org context. The domain it carries is the org's first domain by
 * display name when the list has one, otherwise the same stand-in a bare URL gets (the last used
 * domain, else the first), and in that case it is not a domain of that org. Only a user with no
 * domains at all is unavailable without a request. A failed detail fetch keeps its kind: 403 and 404
 * are unavailable, anything else is a failure the user can retry or report, never an empty result.
 */
export async function resolveAccess(input: ResolveAccessInput): Promise<AccessResult> {
    const { url, domains, fetchDomain, lastUsedDomainId } = input

    let target: AccessDomainRecord | undefined
    let context: AccessContext
    let redirectTo: string | undefined

    switch (url.kind) {
        case 'domain':
            // The list holds only the domains the user has active; a paused or newly shared domain is
            // still theirs to open, so the server decides when the list does not name it.
            target = domains.find((d) => d.id === url.domainId) ?? { id: url.domainId, accountId: 0, displayName: '' }
            context = { kind: 'domain', domainId: url.domainId }
            break
        case 'org':
            target =
                domains.filter((d) => d.accountId === url.orgId).sort(byDisplayName)[0] ??
                domains.find((d) => d.id === lastUsedDomainId) ??
                domains[0]
            context = { kind: 'org', orgId: url.orgId }
            break
        case 'none':
            target = domains.find((d) => d.id === lastUsedDomainId) ?? domains[0]
            context = { kind: 'domain', domainId: target?.id ?? 0 }
            redirectTo = target ? `/domains/${target.id}/dashboard` : undefined
            break
    }

    if (!target) {
        return FORBIDDEN
    }

    const fetched = await fetchDetail(fetchDomain, target.id)
    if (!fetched.ok) {
        return fetched.result
    }
    return { status: 'resolved', context, domain: fetched.domain, redirectTo }
}
